import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Checkbox, Input, Modal, Spin, notification } from 'antd'
import LoadingOutlined from '@ant-design/icons/LoadingOutlined'
import SendOutlined from '@ant-design/icons/SendOutlined'
import { isNil } from 'lodash'
import styled from 'styled-components'
import {
  useSendPreviewMutation,
  useSendSelectedMutation,
} from '../../api/invoice-delivery-api-client'
import {
  BulkUpdateDeliveryStatusResponseDto,
  InvoiceDeliveryItemDto,
  InvoiceDeliveryStatus,
  InvoiceTemplate,
  RecipientOutcome,
  SendPreviewDeliveryDto,
} from '../../api/dto/invoice-delivery.dto'
import { EmailTemplateTag } from './emailTemplates/EmailTemplateTag'
import { countByTemplate } from './emailTemplates/invoiceTemplates'

/* ── Styles ─────────────────────────────────────────────────────────────── */

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`

const GroupTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
`

const GroupHint = styled.div`
  font-size: 12px;
  color: #9ca3af;
`

const GroupNote = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 10px;
`

const LinkButton = styled.button`
  border: 0;
  background: none;
  padding: 0;
  font-size: 13px;
  font-weight: 600;
  color: #01a0a8;
  cursor: pointer;

  &:disabled {
    color: #9ca3af;
    cursor: default;
  }
`

const HiddenBanner = styled.div`
  font-size: 13px;
  color: #92400e;
  background: #fef3c7;
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 10px;
`

const Group = styled.div`
  margin-bottom: 22px;
`

const RowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Card = styled.div<{ $on: boolean; $blocked: boolean }>`
  border: 1px solid ${({ $on }) => ($on ? '#bbf7d0' : '#e5e7eb')};
  background: ${({ $on, $blocked }) =>
    $blocked ? '#f9fafb' : $on ? '#f0fdf4' : '#ffffff'};
  border-radius: 10px;
  overflow: hidden;
  opacity: ${({ $blocked }) => ($blocked ? 0.75 : 1)};
`

const CardMain = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
`

const CardBody = styled.div<{ $clickable: boolean }>`
  flex: 1;
  min-width: 0;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`

const CompanyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const CompanyName = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #111827;
`

const VersionTag = styled.span`
  background: #eff6ff;
  color: #0284c7;
  border-radius: 6px;
  padding: 1px 7px;
  font-size: 12px;
  font-weight: 700;
`

const MetaLine = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const BlockedLine = styled.div`
  font-size: 13px;
  color: #dc2626;
  margin-top: 2px;
`

const RecipientPill = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #0284c7;
  background: #eff6ff;
  border-radius: 9999px;
  padding: 3px 11px;
  white-space: nowrap;
`

const PeopleList = styled.div`
  border-top: 1px dashed #e5e7eb;
  background: #fbfcfe;
  padding: 6px 14px 10px 46px;
  display: flex;
  flex-direction: column;
`

const PersonRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 2px;
  cursor: pointer;
`

const PersonEmail = styled.span`
  font-size: 14px;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RoleTag = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: #6b7280;
  background: #f3f4f6;
  border-radius: 4px;
  padding: 1px 5px;
  text-transform: uppercase;
`

const OutcomeNote = styled.span<{ $color: string }>`
  margin-left: auto;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  white-space: nowrap;
`

const EmptyState = styled.div`
  border: 1px dashed #e5e7eb;
  border-radius: 10px;
  padding: 26px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
`

const ScrollBody = styled.div`
  max-height: 52vh;
  overflow-y: auto;
  padding-right: 4px;
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 4px;
`

const Summary = styled.div`
  font-size: 14px;
  color: #4b5563;
  font-weight: 600;
`

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const CancelButton = styled.button`
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #4b5563;
  border-radius: 8px;
  padding: 9px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`

const ConfirmButton = styled.button`
  border: 0;
  border-radius: 8px;
  padding: 9px 20px;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  background: #16a34a;
  cursor: pointer;

  &:disabled {
    background: #d1d5db;
    cursor: default;
  }
`

const CenterState = styled.div`
  padding: 48px;
  text-align: center;
  color: #6b7280;
`

/* ── Outcome presentation ───────────────────────────────────────────────── */

const DELIVERED_GREEN = '#16a34a'
const FAILURE_RED = '#dc2626'
const OPT_OUT_AMBER = '#d97706'
const PENDING_GREY = '#6b7280'

/*
 * Mirrors EmailDeliveryResults' EVENT_CONFIG so an outcome reads the same in
 * the drawer and here. Kept as labels only — this list needs no icons.
 */
const OUTCOME_LABELS: Record<
  RecipientOutcome,
  { label: string; color: string }
> = {
  delivered: { label: 'Already received', color: DELIVERED_GREEN },
  bounced: { label: 'Bounced', color: FAILURE_RED },
  dropped: { label: 'Dropped', color: FAILURE_RED },
  blocked: { label: 'Blocked', color: FAILURE_RED },
  unsubscribed: { label: 'Unsubscribed', color: OPT_OUT_AMBER },
  spam_reported: { label: 'Marked as spam', color: OPT_OUT_AMBER },
  // Matches the drawer's wording: the address was dispatched to and no webhook
  // has arrived yet. See resolveOutcomeLabel for the never-dispatched case.
  no_response: { label: 'Awaiting confirmation', color: PENDING_GREY },
}

/**
 * How one recipient's state should read in the review list.
 *
 * `no_response` covers two very different situations, and the server tells
 * them apart with `wasDispatched`: an address we emailed that has no webhook
 * yet is genuinely awaiting confirmation, while one never emailed at all has
 * not been sent to. The second happens whenever an address is added to the
 * billing profile or global CC list after the invoice went out — labelling it
 * "Awaiting confirmation" claims an email is in flight that never existed.
 */
function resolveOutcomeLabel(recipient: {
  outcome: RecipientOutcome
  wasDispatched: boolean
}): { label: string; color: string } {
  if (recipient.outcome === 'no_response' && !recipient.wasDispatched) {
    return { label: 'Not yet sent', color: PENDING_GREY }
  }
  return (
    OUTCOME_LABELS[recipient.outcome] ?? {
      label: recipient.outcome,
      color: PENDING_GREY,
    }
  )
}

/* ── Selection state ────────────────────────────────────────────────────── */

type PickedMap = Record<string, Record<string, boolean>>

interface Selection {
  included: Record<string, boolean>
  picked: PickedMap
}

function seedSelection(deliveries: SendPreviewDeliveryDto[]): Selection {
  const included: Record<string, boolean> = {}
  const picked: PickedMap = {}
  deliveries.forEach((d) => {
    if (d.blockedReason) return
    included[d.deliveryId] = d.defaultSelected
    picked[d.deliveryId] = Object.fromEntries(
      d.recipients.map((r) => [r.email, r.defaultSelected])
    )
  })
  return { included, picked }
}

/** Addresses ticked for a delivery, empty when the delivery itself is off. */
function chosenEmails(
  delivery: SendPreviewDeliveryDto,
  selection: Selection
): string[] {
  if (!selection.included[delivery.deliveryId]) return []
  const picks = selection.picked[delivery.deliveryId] ?? {}
  return delivery.recipients.filter((r) => picks[r.email]).map((r) => r.email)
}

interface ReviewBeforeSendModalProps {
  open: boolean
  /** Deliveries to review. Order is preserved in the modal. */
  deliveryIds: string[]
  /** Single scope opens recipients expanded and hides the company filter. */
  scope: 'single' | 'bulk'
  /** The month's deliveries, used to label each card with its email template. */
  monthDeliveries: InvoiceDeliveryItemDto[]
  onClose: () => void
  onSent: (result: BulkUpdateDeliveryStatusResponseDto) => void
}

const ReviewBeforeSendModal: React.FC<ReviewBeforeSendModalProps> = ({
  open,
  deliveryIds,
  scope,
  monthDeliveries,
  onClose,
  onSent,
}) => {
  const deliveryById = useMemo(
    () => new Map(monthDeliveries.map((d) => [d.id, d])),
    [monthDeliveries]
  )
  const hasMixedTemplates = useMemo(() => {
    const sending = deliveryIds.flatMap((id) => deliveryById.get(id) ?? [])
    return countByTemplate(sending).length > 1
  }, [deliveryIds, deliveryById])
  const [preview, setPreview] = useState<SendPreviewDeliveryDto[] | null>(null)
  const [selection, setSelection] = useState<Selection>({
    included: {},
    picked: {},
  })
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [filter, setFilter] = useState('')

  const previewMutation = useSendPreviewMutation()
  const sendMutation = useSendSelectedMutation()
  const { mutate: loadPreview } = previewMutation

  // Refetched per open so the review always reflects current delivery state.
  useEffect(() => {
    if (!open || deliveryIds.length === 0) return
    setPreview(null)
    setFilter('')
    loadPreview(
      { ids: deliveryIds },
      {
        onSuccess: ({ deliveries }) => {
          setPreview(deliveries)
          setSelection(seedSelection(deliveries))
          setExpanded(
            scope === 'single'
              ? Object.fromEntries(deliveries.map((d) => [d.deliveryId, true]))
              : {}
          )
        },
        onError: (error) => {
          notification.error({
            message: 'Could not load the send preview',
            description: error.message,
          })
          onClose()
        },
      }
    )
    // `onClose` is deliberately not a dependency: re-running this on every
    // parent re-render would refetch the preview and wipe the operator's
    // in-progress selection.
  }, [open, deliveryIds, scope, loadPreview])

  const toggleDelivery = useCallback((id: string) => {
    setSelection((prev) => ({
      ...prev,
      included: { ...prev.included, [id]: !prev.included[id] },
    }))
  }, [])

  const togglePerson = useCallback((id: string, email: string) => {
    setSelection((prev) => {
      const picks = { ...(prev.picked[id] ?? {}) }
      picks[email] = !picks[email]
      const anyPicked = Object.values(picks).some(Boolean)
      return {
        // Ticking a person implies the delivery is in; unticking the last one
        // takes it out. Otherwise the footer could promise a send with nobody
        // to send to.
        included: { ...prev.included, [id]: anyPicked },
        picked: { ...prev.picked, [id]: picks },
      }
    })
  }, [])

  const toggleExpanded = useCallback((id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))
  }, [])

  const all = preview ?? []
  const query = filter.trim().toLowerCase()
  const matches = useCallback(
    (d: SendPreviewDeliveryDto) =>
      !query || (d.clientName ?? '').toLowerCase().includes(query),
    [query]
  )

  /*
   * Blocked rows get their own group rather than sitting under "Will send".
   * A delivery that is mid-flight, unmatched or has no recipients is not
   * going anywhere, and counting it in the send heading promises an email
   * that will not be sent. This is also what makes reopening the modal
   * straight after a send honest: the row comes back `sending` and is shown
   * as in-flight instead of offering to send it a second time.
   */
  const blockedDeliveries = all.filter((d) => !isNil(d.blockedReason))
  const sendable = all.filter((d) => isNil(d.blockedReason))
  const unsent = sendable.filter(
    (d) => d.status !== InvoiceDeliveryStatus.Delivered
  )
  const delivered = sendable.filter(
    (d) => d.status === InvoiceDeliveryStatus.Delivered
  )
  const visibleUnsent = unsent.filter(matches)
  const visibleDelivered = delivered.filter(matches)
  const visibleBlocked = blockedDeliveries.filter(matches)

  const payload = useMemo(
    () =>
      all
        .filter((d) => !d.blockedReason)
        .map((d) => ({
          deliveryId: d.deliveryId,
          recipients: chosenEmails(d, selection),
        }))
        .filter((entry) => entry.recipients.length > 0),
    [all, selection]
  )

  /*
   * Counted over every delivery, not just the ones the filter leaves visible —
   * the confirm button must never understate what is about to be sent.
   */
  const firstTimeCount = useMemo(
    () => unsent.reduce((n, d) => n + chosenEmails(d, selection).length, 0),
    [unsent, selection]
  )
  const resendCount = useMemo(
    () => delivered.reduce((n, d) => n + chosenEmails(d, selection).length, 0),
    [delivered, selection]
  )
  const deliveryCount = payload.length
  const peopleCount = firstTimeCount + resendCount

  // Same scope as toggleAllDelivered, or the label contradicts what it does.
  const allDeliveredOn =
    visibleDelivered.length > 0 &&
    visibleDelivered.every(
      (d) => d.blockedReason || selection.included[d.deliveryId]
    )

  /*
   * Scoped to the rows the filter leaves visible. Ticking invoices the
   * operator cannot see would queue duplicate billing documents they never
   * reviewed — and it is a resend, so every one of those is a duplicate.
   */
  const toggleAllDelivered = useCallback(() => {
    setSelection((prev) => {
      const included = { ...prev.included }
      const picked = { ...prev.picked }
      visibleDelivered.forEach((d) => {
        if (d.blockedReason) return
        const next = !allDeliveredOn
        included[d.deliveryId] = next
        // Selecting the group means "send to everyone on it" — otherwise a
        // delivery would turn on with no recipients ticked and send nothing.
        picked[d.deliveryId] = Object.fromEntries(
          d.recipients.map((r) => [r.email, next])
        )
      })
      return { included, picked }
    })
  }, [visibleDelivered, allDeliveredOn])

  const handleConfirm = useCallback(() => {
    if (payload.length === 0) return
    sendMutation.mutate(
      { deliveries: payload },
      {
        onSuccess: (result) => {
          onSent(result)
          onClose()
        },
        onError: (error) => {
          notification.error({
            message: 'Failed to send',
            description: error.message,
          })
        },
      }
    )
  }, [payload, sendMutation, onSent, onClose])

  const renderCard = (d: SendPreviewDeliveryDto) => {
    const blocked = !isNil(d.blockedReason)
    const on = !blocked && !!selection.included[d.deliveryId]
    const picks = selection.picked[d.deliveryId] ?? {}
    const pickedCount = d.recipients.filter((r) => picks[r.email]).length
    const total = d.recipients.length
    const isOpen = !!expanded[d.deliveryId]
    const listed = deliveryById.get(d.deliveryId)
    const isTemplateTagVisible =
      !isNil(listed) &&
      (hasMixedTemplates || listed.invoiceTemplate !== InvoiceTemplate.MONTHLY)

    return (
      <Card key={d.deliveryId} $on={on} $blocked={blocked}>
        <CardMain>
          <Checkbox
            checked={on}
            disabled={blocked}
            onChange={() => toggleDelivery(d.deliveryId)}
          />
          <CardBody
            $clickable={!blocked}
            onClick={() => !blocked && toggleDelivery(d.deliveryId)}
          >
            <CompanyRow>
              <CompanyName>{d.clientName ?? 'Unmatched company'}</CompanyName>
              {isTemplateTagVisible && listed ? (
                <EmailTemplateTag
                  template={listed.invoiceTemplate}
                  paymentType={listed.paymentType}
                />
              ) : null}
              {d.version > 1 && <VersionTag>v{d.version}</VersionTag>}
            </CompanyRow>
            {blocked ? (
              <BlockedLine>{d.blockedReason}</BlockedLine>
            ) : (
              <MetaLine>{d.fileName}</MetaLine>
            )}
          </CardBody>
          {!blocked && total > 0 && (
            <>
              <RecipientPill>
                {on
                  ? `${pickedCount} of ${total} ${total === 1 ? 'recipient' : 'recipients'}`
                  : `${total} ${total === 1 ? 'recipient' : 'recipients'}`}
              </RecipientPill>
              <LinkButton
                type="button"
                onClick={() => toggleExpanded(d.deliveryId)}
              >
                {isOpen ? 'Hide people' : 'Choose people'}
              </LinkButton>
            </>
          )}
        </CardMain>

        {isOpen && !blocked && (
          <PeopleList>
            {d.recipients.map((r) => {
              const config = resolveOutcomeLabel(r)
              return (
                <PersonRow key={r.email} title={r.reason}>
                  <Checkbox
                    checked={!!picks[r.email]}
                    onChange={() => togglePerson(d.deliveryId, r.email)}
                  />
                  <RoleTag>{r.role}</RoleTag>
                  <PersonEmail>{r.email}</PersonEmail>
                  <OutcomeNote $color={config.color}>
                    {config.label}
                  </OutcomeNote>
                </PersonRow>
              )
            })}
          </PeopleList>
        )}
      </Card>
    )
  }

  const isLoading = previewMutation.isPending || isNil(preview)
  const isSending = sendMutation.isPending
  const single = scope === 'single'
  const firstName = all[0]?.clientName

  /*
   * Leads with what is about to happen, not with how the checkboxes are set.
   * The earlier wording ("Unsent invoices are ticked… skipped unless you tick
   * them") described the mechanism and left the operator to work out the
   * outcome, and read badly in the singular.
   */
  const introText = (() => {
    if (single) {
      // A single-scope open can still land on a blocked delivery — most often
      // one the operator just sent, reopened before the webhook settled.
      const only = all[0]
      if (only && !isNil(only.blockedReason)) return only.blockedReason
      return 'This invoice has already been delivered. Choose who should receive it again.'
    }
    const sendable = unsent.length
    if (delivered.length === 0) {
      return `Ready to send ${sendable} ${sendable === 1 ? 'invoice' : 'invoices'}. Review the recipients below, then confirm.`
    }
    const alreadyPart =
      delivered.length === 1
        ? '1 invoice below has already been delivered, so it is left out'
        : `${delivered.length} invoices below have already been delivered, so they are left out`
    if (sendable === 0) {
      return `${alreadyPart[0].toUpperCase()}${alreadyPart.slice(1)} — tick anyone who needs their invoice again.`
    }
    return `Sending ${sendable} ${sendable === 1 ? 'invoice' : 'invoices'}. ${alreadyPart[0].toUpperCase()}${alreadyPart.slice(1)} — tick anyone who needs their invoice again.`
  })()

  const confirmLabel = (() => {
    if (peopleCount === 0) return 'Nothing selected'
    if (single) {
      return `Resend to ${peopleCount} ${peopleCount === 1 ? 'person' : 'people'}`
    }
    return `Send ${deliveryCount} ${deliveryCount === 1 ? 'invoice' : 'invoices'}`
  })()

  const summary = (() => {
    if (peopleCount === 0) return 'No recipients selected'
    const parts = [`${firstTimeCount} first-time`]
    if (delivered.length > 0) {
      parts.push(`${resendCount} resend${resendCount === 1 ? '' : 's'}`)
    }
    return parts.join('  ·  ')
  })()

  /*
   * One warning covering every group, rendered above them rather than inside
   * one. A per-group banner disappears with its own group: filtering to a
   * company that only has blocked deliveries hid the "still queued" notice
   * entirely, while confirm went on dispatching the invoices the filter had
   * hidden. The count a filter conceals is exactly what must stay visible.
   */
  const hiddenCount =
    all.length -
    (visibleUnsent.length + visibleDelivered.length + visibleBlocked.length)

  return (
    <Modal
      open={open}
      title={
        single && firstName ? `Resend to ${firstName}` : 'Review before sending'
      }
      onCancel={onClose}
      footer={null}
      width={760}
      destroyOnClose
    >
      {isLoading ? (
        <CenterState>
          <Spin /> <div style={{ marginTop: 12 }}>Loading recipients…</div>
        </CenterState>
      ) : (
        <>
          <GroupNote>{introText}</GroupNote>

          {!single && all.length > 1 && (
            <Input
              allowClear
              placeholder="Filter by company…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{ marginBottom: 16 }}
            />
          )}

          <ScrollBody>
            {visibleUnsent.length === 0 &&
              visibleDelivered.length === 0 &&
              visibleBlocked.length === 0 && (
                <EmptyState>
                  {query
                    ? `No companies match “${filter.trim()}”`
                    : 'Nothing to send.'}
                </EmptyState>
              )}

            {hiddenCount > 0 && (
              <HiddenBanner>
                {hiddenCount}{' '}
                {hiddenCount === 1 ? 'invoice is' : 'invoices are'} hidden by
                the filter. Confirming still sends everything ticked —{' '}
                {peopleCount} {peopleCount === 1 ? 'recipient' : 'recipients'}{' '}
                across {deliveryCount}{' '}
                {deliveryCount === 1 ? 'invoice' : 'invoices'}.
              </HiddenBanner>
            )}

            {visibleUnsent.length > 0 && (
              <Group>
                <GroupHeader>
                  {/* Both group counts are stated in invoices, so the two
                      headings can be compared at a glance. */}
                  <GroupTitle>
                    Will send · {visibleUnsent.length}{' '}
                    {visibleUnsent.length === 1 ? 'invoice' : 'invoices'}
                  </GroupTitle>
                  <GroupHint>
                    {firstTimeCount}{' '}
                    {firstTimeCount === 1 ? 'recipient' : 'recipients'}
                  </GroupHint>
                </GroupHeader>
                <RowList>{visibleUnsent.map(renderCard)}</RowList>
              </Group>
            )}

            {visibleDelivered.length > 0 && (
              <Group>
                <GroupHeader>
                  <GroupTitle>
                    {/* Counts visible rows, like the other two headings — the
                        filter-hidden total is reported by the banner above. */}
                    Already delivered · {visibleDelivered.length}{' '}
                    {visibleDelivered.length === 1 ? 'invoice' : 'invoices'}
                  </GroupTitle>
                  <LinkButton type="button" onClick={toggleAllDelivered}>
                    {allDeliveredOn ? 'Clear all' : 'Select all'}
                  </LinkButton>
                </GroupHeader>
                <GroupNote>
                  These recipients already have their invoice. Tick anyone who
                  needs another copy — they will receive a duplicate.
                </GroupNote>
                <RowList>{visibleDelivered.map(renderCard)}</RowList>
              </Group>
            )}

            {visibleBlocked.length > 0 && (
              <Group>
                <GroupHeader>
                  <GroupTitle>
                    Cannot send · {visibleBlocked.length}{' '}
                    {visibleBlocked.length === 1 ? 'invoice' : 'invoices'}
                  </GroupTitle>
                  <GroupHint>Not included</GroupHint>
                </GroupHeader>
                <GroupNote>
                  Each row below says why. A delivery that is still sending will
                  be selectable again once it settles.
                </GroupNote>
                <RowList>{visibleBlocked.map(renderCard)}</RowList>
              </Group>
            )}
          </ScrollBody>

          <Footer>
            <Summary>{summary}</Summary>
            <FooterActions>
              <CancelButton
                type="button"
                onClick={onClose}
                disabled={isSending}
              >
                Cancel
              </CancelButton>
              <ConfirmButton
                type="button"
                onClick={handleConfirm}
                disabled={peopleCount === 0 || isSending}
              >
                {isSending ? <LoadingOutlined /> : <SendOutlined />}{' '}
                {confirmLabel}
              </ConfirmButton>
            </FooterActions>
          </Footer>
        </>
      )}
    </Modal>
  )
}

export { ReviewBeforeSendModal }
